import api from '../../API';
import styles from './Config.module.css';
import { useRevalidator, useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../providers/AuthContext';
import { useNetworkData } from '../../hooks/network/useNetworkData';
import { queryClient } from '../../providers/AuthContext';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import SetupPageLayout from '../../components/setup/SetupPageLayout';
import { fetchApiQuery } from '../../utils/apiResponse';
import HubAllocationPanel from '../../components/network/HubAllocationPanel';
import { getAdminStep } from '../../constants/setupRoadmapSteps';

const initialStep = getAdminStep('initial');

function InitialConfig(){
    const navigate = useNavigate();
    const { user, pending, refreshBdsaInfo } = useAuth();
    const revalidator = useRevalidator();
    const queryClient = useQueryClient();
    const { local:localData, isLoading, defaultNetworkName, connections } = useNetworkData();

    // Mutations
    const updateConfigMutation = useMutation({
        mutationFn: (submitData) => api.put('network/local', {body: submitData}),
        onSuccess: async (response) => {
            if (response.status === 200) {
                await refreshBdsaInfo();
                await queryClient.invalidateQueries({ queryKey: ['local-info'] });
                await queryClient.invalidateQueries({ queryKey: ['local-connections'] });
                revalidator.revalidate();
            }
        },
    });

    const clearNetworkInfoMutation = useMutation({
        mutationFn: () => api.delete('network/local/clear'),
        onSuccess: async (response) => {
            if (response.status === 200) {
                alert(`Successfully cleared ${response.data.deletedCount || 0} record(s).`);
                await refreshBdsaInfo();
                await queryClient.invalidateQueries({ queryKey: ['local-info'] });
                await queryClient.invalidateQueries({ queryKey: ['local-connections'] });
                revalidator.revalidate();
            } else {
                const errorMsg = response.data?.error || response.statusText || 'Unknown error';
                alert(`Failed to clear records (${response.status}): ${errorMsg}`);
            }
        },
        onError: (error) => {
            console.error('Error clearing records:', error);
            alert('Error clearing records: ' + (error.message || 'Unknown error'));
        },
    });
    
    // Extract data for processing
    const apiUrl = localData?.apiUrl || '';

    const [isApiUrlEditing, setIsApiUrlEditing] = useState(false);
    
    // Determine if hub mode (check isHub field on local node)
    const initialIsHub = localData?.isHub || false;
    // Initialize with function to handle undefined hubData on first render
    const [isHub, setIsHub] = useState(() => initialIsHub);
    
    // Get data from appropriate record (hub takes precedence if exists, otherwise local)
    const nodeData = localData || {};
    const initialAllowProtectedOperations = nodeData?.allowProtectedOperations || false;
    const [allowProtectedOperations, setAllowProtectedOperations] = useState(initialAllowProtectedOperations);
    
    // Network name (for hub) - get from networkName field directly (flattened in new API)
    const initialNetworkName = localData?.networkName || '';
    const [networkNameValue, setNetworkNameValue] = useState(initialNetworkName);
    
    // Local site name - get from local data
    const initialLocalSiteName = localData?.siteName || '';
    const [siteNameValue, setSiteNameValue] = useState(initialLocalSiteName);
    
    
    
    // Track initial form values for change detection - memoize to prevent recreation
    const initialValues = useMemo(() => ({
        networkName: initialNetworkName,
        siteName: initialLocalSiteName,
        apiUrl: apiUrl || '',
        contactName: nodeData?.contactName || '',
        contactEmail: nodeData?.contactEmail || '',
        isHub: initialIsHub,
        allowProtectedOperations: initialAllowProtectedOperations
    }), [initialNetworkName, initialLocalSiteName, apiUrl, nodeData?.contactName, nodeData?.contactEmail, initialIsHub, initialAllowProtectedOperations]);
    
    // Track current form values
    const [currentValues, setCurrentValues] = useState(() => ({
        networkName: initialNetworkName,
        siteName: initialLocalSiteName,
        apiUrl: apiUrl || '',
        contactName: nodeData?.contactName || '',
        contactEmail: nodeData?.contactEmail || '',
        isHub: initialIsHub,
        allowProtectedOperations: initialAllowProtectedOperations
    }));
    
    // Check if form has changed - compare values carefully
    const hasChanges = useMemo(() => {
        // Normalize values for comparison (handle undefined/null/empty string differences)
        const normalize = (val) => {
            if (val === undefined || val === null) return '';
            if (typeof val === 'boolean') return val;
            return String(val).trim();
        };
        
        const normalizedCurrent = {
            networkName: normalize(currentValues.networkName),
            siteName: normalize(currentValues.siteName),
            apiUrl: normalize(currentValues.apiUrl),
            contactName: normalize(currentValues.contactName),
            contactEmail: normalize(currentValues.contactEmail),
            isHub: Boolean(currentValues.isHub),
            allowProtectedOperations: Boolean(currentValues.allowProtectedOperations)
        };
        
        const normalizedInitial = {
            networkName: normalize(initialValues.networkName),
            siteName: normalize(initialValues.siteName),
            apiUrl: normalize(initialValues.apiUrl),
            contactName: normalize(initialValues.contactName),
            contactEmail: normalize(initialValues.contactEmail),
            isHub: Boolean(initialValues.isHub),
            allowProtectedOperations: Boolean(initialValues.allowProtectedOperations)
        };
        
        return JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial);
    }, [currentValues, initialValues]);
    
    
    // Update state when data changes
    useEffect(() => {
        if (!isLoading && localData !== undefined) {
            const newApiUrl = localData?.apiUrl || '';
            const newIsHub = localData?.isHub || false;
            const newNodeData = localData || {};
            // Get network name: now flattened in API response
            const newNetworkName = localData?.networkName || '';
            const newLocalSiteName = localData?.siteName || '';
            const newAllowProtectedOperations = newNodeData.allowProtectedOperations || false;
            
            setIsHub(newIsHub);
            setAllowProtectedOperations(newAllowProtectedOperations);
            setNetworkNameValue(newNetworkName);
            setSiteNameValue(newLocalSiteName);
            setIsApiUrlEditing(false);
            
            // Reset current values to match loaded data
            setCurrentValues({
                networkName: newNetworkName,
                siteName: newLocalSiteName,
                apiUrl: newApiUrl,
                contactName: newNodeData.contactName || '',
                contactEmail: newNodeData.contactEmail || '',
                isHub: newIsHub,
                allowProtectedOperations: newAllowProtectedOperations
            });
        }
    }, [localData, isLoading]);

    // Determine saved vs current mode - calculate before early returns
    const savedIsHub = localData?.isHub || false;
    
    // Check for connections to disable toggle
    const shouldDisableToggle = connections?.length > 0;
    const shouldDisableHubName = isHub && connections?.length > 0;
    
    // Check if initial configuration is complete (all required fields filled)
    const isInitialConfigComplete = useMemo(() => {
        if (!localData) return false;
        const hasSiteName = localData.siteName && localData.siteName.trim() !== '';
        const hasApiUrl = localData.apiUrl && localData.apiUrl.trim() !== '';
        if (!hasSiteName || !hasApiUrl) return false;
        const isHub = localData.isHub || false;
        if (isHub) {
            const hasNetworkName = localData.networkName && localData.networkName.trim() !== '';
            if (!hasNetworkName) return false;
        }
        return true;
    }, [localData]);
    
    
    async function handleSubmit(event){
        event.preventDefault();
        
        // Build submit data manually to ensure all required fields are included
        const apiUrlValue = currentValues.apiUrl || apiUrl || '';
        
        // Get networkName (for hub) from input or current state
        const networkNameInput = document.getElementById('networkName');
        const networkNameSubmitValue = networkNameInput?.value || networkNameValue || (isHub ? 'Girder' : '');
        
        // Get siteName (local site name) from input or current state
        const siteNameInput = document.getElementById('siteName');
        const siteNameSubmitValue = siteNameInput?.value || siteNameValue || '';
        
        // Get contact fields
        const contactNameInput = document.getElementById('contactName');
        const contactNameValue = contactNameInput?.value || '';
        
        const contactEmailInput = document.getElementById('contactEmail');
        const contactEmailValue = contactEmailInput?.value || '';
        
        // Build submit data - using body format that API.js will convert to FormData
        const submitData = {
            networkName: networkNameSubmitValue,
            displayName: siteNameSubmitValue,  // API expects displayName field
            apiUrl: apiUrlValue,
            contactName: contactNameValue || '',
            contactEmail: contactEmailValue || '',
            isHub: isHub ? 'true' : 'false',
            allowProtectedOperations: allowProtectedOperations ? 'true' : 'false'
        };
        
        console.log('Submitting form data:', submitData);
        console.log('Form values check:', {
            apiUrlFromState: currentValues.apiUrl,
            networkNameFromInput: networkNameInput?.value,
            networkNameFromState: networkNameValue,
            siteNameFromInput: siteNameInput?.value,
            siteNameFromState: siteNameValue,
            isHub: isHub
        });
        
        updateConfigMutation.mutate(submitData, {
            onSuccess: (response) => {
                if (response.status !== 200) {
                    console.error('Failed to save configuration:', response);
                    const errorMsg = response.data || response.statusText || 'Unknown error';
                    alert(`Failed to save configuration (${response.status}): ${errorMsg}`);
                }
            },
            onError: (error) => {
                console.error('Error saving configuration:', error);
                alert('Error saving configuration: ' + (error.message || 'Unknown error'));
            },
        });
    }
    
    // Handler to update current values when form fields change
    const handleInputChange = (field, value) => {
        setCurrentValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleToggle = () => {
        if (!shouldDisableToggle) {
            const newIsHub = !isHub;
            setIsHub(newIsHub);
            handleInputChange('isHub', newIsHub);
            // Update networkName when switching to hub mode
            if (newIsHub) {
                // If we were previously saved as hub and have a network name, preserve it
                // Otherwise use the default from root collection
                const savedNetworkName = localData?.networkName;
                const newNetworkName = (savedNetworkName && savedNetworkName !== '') 
                    ? savedNetworkName 
                    : (defaultNetworkName || 'Girder');
                setNetworkNameValue(newNetworkName);
                handleInputChange('networkName', newNetworkName);
            } else {
                // When switching to spoke mode, clear network name (it will come from hub when connected)
                setNetworkNameValue('');
                handleInputChange('networkName', '');
            }
        }
    };

    // Handle loading state: while auth is pending or data is loading
    if (pending || isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <SetupPageLayout
            stepNumber={initialStep.number}
            totalSteps={initialStep.total}
            title="Initial Server Configuration"
            description="Configure your BDSA server's basic settings including network role, node name, and contact information. Once you save your configuration, you can proceed to the next step."
            onNext={() => navigate('/admin/setup/network')}
            onPrevious={() => navigate('/admin/setup')}
            nextDisabled={!isInitialConfigComplete}
            nextLabel="Next: Network Connectivity"
        >
            <h1>Initial Server Configuration</h1>

            <form onSubmit = {handleSubmit} className={styles.form}>
            {/* Hub/Spoke selection - First */}
            <div style={{marginBottom: '2em'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '1em', marginBottom: '0.5em', flexWrap: 'wrap'}}>
                    <label style={{fontWeight: 'bold', whiteSpace: 'nowrap'}}>
                        Network Role:
                    </label>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.75em', position: 'relative'}}>
                        <span style={{fontSize: '0.9em', color: !isHub ? '#333' : '#999'}}>Spoke</span>
                        <div 
                            className={styles.toggle}
                            onClick={handleToggle}
                            role="button"
                            tabIndex={shouldDisableToggle ? -1 : 0}
                            onKeyDown={(e) => {
                                if (!shouldDisableToggle && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    handleToggle();
                                }
                            }}
                            style={{
                                backgroundColor: isHub ? '#1f6feb' : '#ccc',
                                opacity: shouldDisableToggle ? 0.5 : 1,
                                cursor: shouldDisableToggle ? 'not-allowed' : 'pointer',
                            }}
                            title={shouldDisableToggle ? 'Switching network roles is not possible while there are pending or active connections. Please cancel or delete all connections before switching modes.' : ''}
                        >
                            <div 
                                className={styles.toggleSlider}
                                style={{
                                    transform: isHub ? 'translateX(24px)' : 'translateX(0px)',
                                }}
                            />
                        </div>
                        <span style={{fontSize: '0.9em', color: isHub ? '#333' : '#999'}}>Hub</span>
                    </div>
                </div>
                {/* Hidden input for form submission */}
                <input type='hidden' name='isHub' value={isHub ? 'true' : 'false'} />
                <div style={{marginTop: '0.75em', fontSize: '0.9em', color: '#666', lineHeight: '1.5'}}>
                    {isHub ? (
                        <p style={{margin: '0.5em 0'}}>
                            <strong>Hub mode:</strong> This installation will act as the hub in a hub-spoke network. 
                            Other BDSA installations (spokes) will be able to connect to this hub, and you will need to accept 
                            incoming connection requests from remote spokes.
                        </p>
                    ) : (
                        <p style={{margin: '0.5em 0'}}>
                            <strong>Spoke mode:</strong> This installation will act as a spoke in a hub-spoke network arrangement.
                            You connect to a hub for discovery and mediation. Peer data can load Direct from the source node or Proxied through the hub if a firewall blocks direct access.
                        </p>
                    )}
                </div>
            </div>
            
            {/* Modern form inputs */}
            <div className={styles.formSection}>
                {isHub && (
                    <>
                        <label className={styles.label} htmlFor="networkName">
                            Network Name:
                        </label>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2em', alignItems: 'center'}}>
                            <div>
                                <input 
                                    id="networkName"
                                    name='networkName' 
                                    type='text' 
                                    className={styles.input}
                                    placeholder={defaultNetworkName || 'Girder'}
                                    defaultValue={localData?.networkName || defaultNetworkName || 'Girder'}
                                    onChange={(e) => {
                                        setNetworkNameValue(e.target.value);
                                        handleInputChange('networkName', e.target.value);
                                    }}
                                    required
                                    disabled={shouldDisableHubName}
                                    title={shouldDisableHubName ? 'Network name cannot be changed while there are active or pending connections. Please disconnect all spokes before changing the network name.' : ''}
                                />
                                {shouldDisableHubName && (
                                    <div style={{fontSize: '0.85em', color: '#666', marginTop: '0.5em', fontStyle: 'italic'}}>
                                        Network name cannot be changed while there are active or pending connections.
                                    </div>
                                )}
                            </div>
                            <div className={styles.preview}>
                                <div className={styles.previewContent}>
                                    This is the <strong>{networkNameValue || defaultNetworkName || 'Girder'} Network</strong> hub.
                                </div>
                            </div>
                        </div>
                        <HubAllocationPanel />
                    </>
                )}
                
                <label className={styles.label} htmlFor="siteName">
                    Local Site Name:
                </label>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2em', alignItems: 'center'}}>
                    <div>
                        <input 
                            id="siteName"
                            name='siteName' 
                            type='text' 
                            className={styles.input}
                            placeholder='Enter site name'
                            defaultValue={localData?.siteName || ''}
                            onChange={(e) => {
                                setSiteNameValue(e.target.value);
                                handleInputChange('siteName', e.target.value);
                            }}
                            required
                        />
                    </div>
                    {(() => {
                        // Show preview if:
                        // 1. Hub mode (always show)
                        // 2. Spoke mode with a valid network identifier (not "NetworkXX")
                        const networkIdentifier = isHub 
                            ? `${networkNameValue || defaultNetworkName || 'Girder'}00`
                            : nodeData.networkIdentifier;
                        const shouldShow = isHub || (networkIdentifier && networkIdentifier !== 'NetworkXX');
                        
                        if (!shouldShow) return null;
                        
                        return (
                            <div className={styles.preview}>
                                <div className={styles.previewContent}>
                                    <>This is node <strong> {networkIdentifier}</strong>, part of the 
                                    <strong> {networkNameValue || localData?.networkName || 'Network'}</strong> Network</>
                                </div>
                            </div>
                        );
                    })()}
                </div>
                
                <label className={styles.label} htmlFor="apiUrl">
                    API URL:
                </label>
                <div className={styles.apiUrlRow}>
                    <input 
                        id="apiUrl"
                        name='apiUrl' 
                        type='url' 
                        className={styles.input}
                        placeholder='https://example.com/api'
                        value={currentValues.apiUrl}
                        onChange={(e) => {
                            if (!isApiUrlEditing) return;
                            handleInputChange('apiUrl', e.target.value);
                        }}
                        required
                        disabled={!isApiUrlEditing}
                    />
                    <div className={styles.apiUrlButtons}>
                        <button
                            type="button"
                            className={styles.apiUrlButton}
                            onClick={() => setIsApiUrlEditing(v => !v)}
                            aria-pressed={isApiUrlEditing}
                        >
                            {isApiUrlEditing ? 'Lock' : 'Edit'}
                        </button>
                        <button
                            type="button"
                            className={styles.apiUrlButton}
                            onClick={() => {
                                if (!isApiUrlEditing) return;
                                const origin = (window.location?.origin || '').replace(/\/$/, '');
                                handleInputChange('apiUrl', `${origin}/dsa/api/v1`);
                            }}
                            disabled={!isApiUrlEditing}
                        >
                            Use current URL
                        </button>
                    </div>
                </div>
                {isApiUrlEditing && (
                    <div className={styles.apiUrlHint}>
                        This must be reachable by other nodes; avoid localhost/host.docker.internal for cross-machine connections.
                    </div>
                )}
                
                <label className={styles.label} htmlFor="contactName">
                    Contact Name:
                </label>
                <input 
                    id="contactName"
                    name='contactName' 
                    type='text' 
                    className={styles.input}
                    placeholder='Contact name'
                    defaultValue={nodeData?.contactName || ''}
                    onChange={(e) => handleInputChange('contactName', e.target.value)}
                />
                
                <label className={styles.label} htmlFor="contactEmail">
                    Contact Email:
                </label>
                <input 
                    id="contactEmail"
                    name='contactEmail' 
                    type='email' 
                    className={styles.input}
                    placeholder='contact@example.com'
                    defaultValue={nodeData?.contactEmail || ''}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                />
            </div>
            
            <div style={{marginTop: '1.5em', marginBottom: '1em'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: '0.5em', cursor: 'pointer'}}>
                    <input 
                        type='checkbox' 
                        checked={allowProtectedOperations}
                        onChange={(e) => {
                            setAllowProtectedOperations(e.target.checked);
                            handleInputChange('allowProtectedOperations', e.target.checked);
                        }}
                    />
                    <span>Allow protected operations (for testing)</span>
                </label>
                <div style={{marginTop: '0.5em', fontSize: '0.85em', color: '#666', fontStyle: 'italic', marginLeft: '1.75em'}}>
                    When enabled, operations on protected BDSA folders and items will be allowed even when isNetworkFolder/isNetworkItem is set.
                </div>
                {/* Hidden input to ensure value is always submitted even when checkbox is unchecked */}
                <input type='hidden' name='allowProtectedOperations' value={allowProtectedOperations ? 'true' : 'false'} />
            </div>
            <div className={styles.unsavedChangesContainer}>
                {hasChanges && (
                    <div className={styles.unsavedChangesBanner}>
                        <span className={styles.unsavedIcon}>●</span>
                        <span>You have unsaved changes</span>
                    </div>
                )}
                {!hasChanges && isInitialConfigComplete && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5em',
                        backgroundColor: '#d4edda',
                        border: '1px solid #28a745',
                        borderRadius: '6px',
                        padding: '10px 14px',
                        fontSize: '14px',
                        color: '#155724',
                        fontWeight: '500'
                    }}>
                        <span>✓</span>
                        <span>Configuration saved! You can proceed to the next step.</span>
                    </div>
                )}
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '1.5em'
            }}>
                <button 
                    type='submit' 
                    className={styles.saveButton}
                    disabled={!hasChanges || updateConfigMutation.isPending}
                    onClick={(e) => {
                        if (!hasChanges) {
                            e.preventDefault();
                            return false;
                        }
                    }}
                >
                    {updateConfigMutation.isPending ? 'Saving...' : 'Save changes'}
                </button>
                <button
                    type='button'
                    onClick={() => {
                        const confirmMessage = 'Are you sure you want to reset the local network configuration?\n\n' +
                            'This will delete all network_node records with role="local". ' +
                            'This will delete your network configuration and hub folder tracking. ' +
                            'This action cannot be undone.\n\n' +
                            'Use for testing purposes only.';
                        if (!window.confirm(confirmMessage)) {
                            return;
                        }
                        clearNetworkInfoMutation.mutate();
                    }}
                    disabled={clearNetworkInfoMutation.isPending}
                    style={{
                        background: '#dc3545',
                        color: '#fff',
                        border: '1px solid #dc3545',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        cursor: clearNetworkInfoMutation.isPending ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: clearNetworkInfoMutation.isPending ? 0.6 : 1
                    }}
                >
                    {clearNetworkInfoMutation.isPending ? 'Resetting...' : 'Reset'}
                </button>
            </div>
        </form>
        </SetupPageLayout>
    )
}

async function Loader(){
    // Prefetch data using the same query keys as useNetworkData to share cache
    const connectionsData = await fetchApiQuery(queryClient, {
        queryKey: ['local-connections'],
        queryFn: () => api.get('network/connections'),
        staleTime: 5 * 60 * 1000, // Match useNetworkData config
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    }).catch(() => null);

    const localData = await fetchApiQuery(queryClient, {
        queryKey: ['local-info'],
        queryFn: () => api.get('network/local'),
        staleTime: 5 * 60 * 1000, // Match useNetworkData config
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    }).catch(() => null);

    // Extract data in the same format as useNetworkData returns
    const connections = connectionsData?.status === 200 ? (connectionsData.data || []) : [];
    // API returns flattened structure directly, not wrapped in local/hub
    const local = localData?.status === 200 ? (localData.data || null) : null;

    return { local, connections };
}

export default InitialConfig;
export { Loader };
